import axios, { AxiosInstance } from "axios";
import qs from "qs";
import { getAccessToken } from "@/utils/token-cookie";
import {
  endSession,
  isAccessTokenExpired,
  isAuthUrl,
  refreshAccessToken,
  toRefreshFailureError,
} from "@/utils/token-refresher";

/**
 * 예전에는 이 파일이 자체 갱신 로직(refreshAuthToken / isRefreshing / failedQueue)을
 * 들고 있었다. uno-axios의 갱신과 완전히 분리돼 있어서
 *
 *  - 복귀 직후 두 스택이 동시에 /auth/refresh를 쏘고,
 *  - 갱신이 200인데 응답 키가 안 맞으면 조용히 넘어가 만료된 토큰으로 재시도하고,
 *  - 갱신이 실패해도 로그아웃 경로가 없어 401 토스트만 반복해서 떴다.
 *
 * 갱신은 전부 token-refresher가 맡는다. 여기서는 헤더를 붙이고, 401이면 한 번
 * 갱신 후 재시도하는 것까지만 한다.
 */

// 기존 import 경로 유지(my-account 등). 예전에는 document.cookie를 직접 파싱했다.
export const getAuthTokenFromCookies = (): string | null => {
  return getAccessToken() ?? null;
};

// 30초를 넘길 수 있는 쓰기 요청(오더시트 저장·제출 등)에 개별 config 로 주는 타임아웃.
// API 앞 ALB(alb-pathrade-api-prod) idle timeout 이 180초라 그보다 길게 잡아도 ALB 가 먼저 끊는다.
export const LONG_WRITE_TIMEOUT_MS = 3 * 60 * 1000;

const axiosClient: AxiosInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  // uno-axios와 같은 30초. 예전에는 timeout이 없어(axios 기본값 0) 응답이 영영 안 오는
  // 요청이 그대로 매달려 있었다. 탭을 오래 띄워 둔 뒤(세션 만료 무렵) 돌아오면 커넥션
  // 풀에 죽은 소켓이 남아 있는데, 그 위로 나간 요청은 에러도 안 나고 pending으로 멈춘다.
  // 이 인스턴스를 쓰는 쿼리 상당수가 useSuspenseQuery라 화면은 로딩 폴백에서 못 빠져나오고,
  // 새로고침 말고는 복구가 안 됐다. 끊어 주면 react-query가 재시도하면서 새 소켓으로 붙는다.
  // 업로드처럼 30초를 넘길 수 있는 호출은 개별 config에서 timeout을 늘린다(uno-axios와 동일한 방식).
  timeout: 30 * 1000,
  paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
});

axiosClient.interceptors.request.use(async (config) => {
  // 만료된 걸 알면서 보내지 않는다. 401 라운드트립 자체가 없어야 사용자 눈에
  // 401이 닿을 일이 없다.
  if (!isAuthUrl(config.url) && isAccessTokenExpired()) {
    try {
      await refreshAccessToken();
    } catch (e) {
      throw toRefreshFailureError(e);
    }
  }

  const token = getAuthTokenFromCookies();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axiosClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;
    const isRefreshable =
      error.response?.status === 401 &&
      originalRequest &&
      !isAuthUrl(originalRequest.url);

    if (isRefreshable && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        await refreshAccessToken();
      } catch (e) {
        // terminal이면 JWT_TOKEN_REFRESH_FAILED 모양으로 바뀌고 세션이 정리된다.
        // axios-client-error.ts의 toErrorResponse와 _app의 AppErrorFallback이
        // 이미 이해하는 모양이라 토스트 문구도 그대로 살아난다.
        return Promise.reject(toRefreshFailureError(e));
      }
      // 요청 인터셉터가 새 토큰으로 Authorization을 다시 만든다.
      return axiosClient(originalRequest);
    }

    // 갱신 후 재시도까지 했는데도 401이면 세션이 죽은 것이다.
    // (react-query를 거치지 않고 axiosClient를 직접 부르는 호출부까지 덮는다)
    if (isRefreshable) {
      endSession();
    }

    return Promise.reject(error);
  }
);

export default axiosClient;
